import { ChartBarIcon, ExclamationTriangleIcon, AdjustmentsHorizontalIcon, ClockIcon } from '@heroicons/react/24/outline';
import { KnowledgeLink } from '@/components/ui/KnowledgeLink';

interface AggregatedReturnsEmptyStateProps {
    /** Whether any collection/tag filters are active */
    hasFilters?: boolean;
    /** Callback to reset filters */
    onResetFilters?: () => void;
    /** Whether the data is currently loading */
    isLoading?: boolean;
    /** Whether all ledgers are excluded (debt ledgers) */
    allLedgersExcluded?: boolean;
    /** Number of excluded ledgers */
    excludedCount?: number;
    /** Whether all values are null (insufficient data) */
    allValuesNull?: boolean;
    /** Label for the metric (MWRR) */
    metricLabel: string;
    /** Minimum height to match chart container and prevent layout shift */
    minHeight?: number;
}

/**
 * Empty state component for the aggregated returns chart (MWRR).
 * Shows different messages based on:
 * - Loading state
 * - All ledgers excluded (debt ledgers)
 * - All values null (insufficient data)
 * - Filters applied but no data
 * - No data available
 */
export function AggregatedReturnsEmptyState({
    hasFilters = false,
    onResetFilters,
    isLoading = false,
    allLedgersExcluded = false,
    excludedCount = 0,
    allValuesNull = false,
    metricLabel,
    minHeight,
}: AggregatedReturnsEmptyStateProps) {
    const containerStyle = minHeight ? { minHeight } : undefined;

    // Loading state
    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-center" style={containerStyle}>
                <div className="animate-pulse">
                    <ChartBarIcon className="h-12 w-12 text-gray-400 dark:text-gray-500" />
                </div>
                <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                    Loading {metricLabel} data...
                </p>
            </div>
        );
    }

    // All ledgers excluded (debt ledgers)
    if (allLedgersExcluded) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-center" style={containerStyle}>
                <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                    <ExclamationTriangleIcon className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                </div>
                <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                    {metricLabel} Not Available
                </h3>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 text-center max-w-sm">
                    {excludedCount > 0
                        ? `All ${excludedCount} ${excludedCount === 1 ? 'ledger is a' : 'ledgers are'} debt ledger${excludedCount === 1 ? '' : 's'} and excluded from ${metricLabel} analysis.`
                        : `All selected ledgers are debt ledgers and excluded from ${metricLabel} analysis.`}
                </p>
                <p className="mt-2 text-xs text-gray-400 dark:text-gray-500 text-center max-w-sm">
                    {metricLabel} is calculated for asset ledgers only.
                </p>
                {hasFilters && onResetFilters && (
                    <button
                        onClick={onResetFilters}
                        className="mt-4 px-4 py-2 text-sm font-medium text-accent hover:text-accent/80 transition-colors"
                    >
                        Reset Filters
                    </button>
                )}
            </div>
        );
    }

    // All values null (insufficient data)
    if (allValuesNull) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-center" style={containerStyle}>
                <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <ClockIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                    Insufficient Data for {metricLabel}
                </h3>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 text-center max-w-sm">
                    {metricLabel} requires at least 3 months of historical data to calculate.
                </p>
                <p className="mt-2 text-xs text-gray-400 dark:text-gray-500 text-center max-w-sm">
                    Continue tracking your portfolio to see {metricLabel} returns over time.
                </p>
                <p className="mt-3">
                    <KnowledgeLink page="mwrr">What is MWRR?</KnowledgeLink>
                </p>
                {hasFilters && onResetFilters && (
                    <button
                        onClick={onResetFilters}
                        className="mt-4 px-4 py-2 text-sm font-medium text-accent hover:text-accent/80 transition-colors"
                    >
                        Reset Filters
                    </button>
                )}
            </div>
        );
    }

    // Has filters but no data
    if (hasFilters) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-center" style={containerStyle}>
                <AdjustmentsHorizontalIcon className="h-12 w-12 text-gray-400 dark:text-gray-500" />
                <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                    No {metricLabel} Data
                </h3>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 text-center max-w-sm">
                    No {metricLabel} data available for the selected filters.
                </p>
                {onResetFilters && (
                    <button
                        onClick={onResetFilters}
                        className="mt-4 px-4 py-2 text-sm font-medium text-accent hover:text-accent/80 transition-colors"
                    >
                        Reset Filters
                    </button>
                )}
            </div>
        );
    }

    // No data available
    return (
        <div className="flex flex-col items-center justify-center py-12 text-center" style={containerStyle}>
            <ChartBarIcon className="h-12 w-12 text-gray-400 dark:text-gray-500" />
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                No {metricLabel} Data Yet
            </h3>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 text-center max-w-sm">
                Add cash flows to your assets to unlock this view.
            </p>
            <p className="mt-3">
                <KnowledgeLink page="mwrr">What is MWRR?</KnowledgeLink>
            </p>
        </div>
    );
}
